<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { arrayToMap } from "@/api/query";
import { useScopeReports } from "@/api/report-query";
import { useSpaces } from "@/api/space-query";
import AppDataTable from "@/components/common/AppDataTable.vue";
import DesktopOnly from "@/components/common/DesktopOnly.vue";
import Frame from "@/components/common/Frame.vue";
import SubscriptionToggleButton from "@/components/common/SubscriptionToggleButton.vue";
import CreateReportDialog from "@/components/dialogs/CreateReportDialog.vue";
import ReportNavButton from "@/components/navigation/ReportNavButton.vue";
import ScopeGuard from "@/components/permission/ScopeGuard.vue";
import VisibilityGuard from "@/components/permission/VisibilityGuard.vue";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import { useDrawerState, useDrawerStore } from "@/stores";
import { useScopeStore } from "@/stores/scope";
import { mdiCheckCircleOutline, mdiTextBoxMultipleOutline } from "@mdi/js";
import { Button, Icon, LinkButton, makeColumnHelper, makeFacetedMapping } from "@soft-db/ui";
import { storeToRefs } from "pinia";

type ReportRowData = {
	id: string;
	title: string;
	description: string;
	enabled: boolean;
	space?: { id: string; name: string };
	isSubscribed: boolean;
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Generic users cannot subscribe to repports
const { currentUser } = useCurrentUser();
const canSubscribe = computed(() => currentUser.value?.auth.type === "email");

const { toggleDrawer } = useDrawerStore();
function changePage(selection: DrawerSelection) {
	router.push({
		name: "reports-id-settings",
		params: { id: selection.id },
		query: { ...route.query },
	});
	toggleDrawer();
}

const { scope } = storeToRefs(useScopeStore());
const { reports, isPending: reportsLoading } = useScopeReports(scope);
const { spaces, isLoading: spacesLoading } = useSpaces();

const spacesMap = computed(() => arrayToMap(spaces.value || []));
const isLoading = computed(() => reportsLoading.value || spacesLoading.value);

const reportRows = computed<ReportRowData[]>(() => {
	if (isLoading.value || !reports.value) return [];

	return reports.value
		.map((report) => {
			const space = report.spaceId
				? { id: report.spaceId, name: spacesMap.value.get(report.spaceId)?.name || "" }
				: undefined;

			return {
				id: report.id,
				title: report.title,
				description: report.description,
				enabled: report.enabled,
				space,
				isSubscribed: report.isSubscribed,
			};
		})
		.sort((a, b) => a.title.localeCompare(b.title));
});

const createReportDialog = ref(false);
function openCreateReportDialog() {
	createReportDialog.value = true;
}

function goToReport(id: string) {
	router.push({ name: "reports-id-settings", params: { id } });
}

const filtersDef = computed(() => {
	return makeFacetedMapping<ReportRowData>({
		enabled: {
			name: t("report.enabled"),
			choices: {
				true: {
					name: t("common.yes"),
				},
				false: {
					name: t("common.no"),
				},
			},
		},
		space: {
			name: t("object.space"),
			allowMultiple: true,
			allowSearch: true,
			choices: { "": { name: t("space.noSpace") } },
		},
	});
});
const drawerState = useDrawerState();
const { filter } = storeToRefs(drawerState);
const fieldFilters = drawerState.getFilters(Object.keys(filtersDef.value));

const { Column } = makeColumnHelper<ReportRowData>();
</script>

<template>
	<DrawerLayout mobileOnly>
		<template #drawer>
			<Drawer :objects="['report']" @click="changePage" />
		</template>
		<Frame class="px-0!" topMargin>
			<AppDataTable
				v-model:filter="filter"
				v-model:fieldFilters="fieldFilters"
				:filtersDef="filtersDef"
				:data="reportRows"
				:loading="isLoading"
				stateStorage="url"
			>
				<template #title>
					<h1 class="flex items-center gap-2 text-center capitalize">
						<Icon :d="mdiTextBoxMultipleOutline" />
						{{ t("object.report", 2) }}
					</h1>
				</template>

				<template #action>
					<VisibilityGuard permission="report.create">
						<Button size="sm" variant="outline" @click="openCreateReportDialog">
							{{ t("actions.createReport") }}
						</Button>
					</VisibilityGuard>
				</template>

				<template #columns>
					<Column
						v-slot="{ row: { original: report } }"
						field="title"
						:header="t('common.name')"
						enableSorting
						enableGlobalFilter
						mobileMain
					>
						<div class="flex min-h-10 flex-col justify-center">
							<div class="flex items-center gap-2">
								<h5>
									{{ report.title }}
								</h5>
								<span class="text-xs font-semibold opacity-50">#{{ report.id }}</span>
							</div>
							<span v-if="report.description" class="inline-block max-w-80 text-xs">
								{{ report.description }}
							</span>
						</div>
					</Column>

					<DesktopOnly>
						<ScopeGuard scope="distributor">
							<Column
								id="space"
								v-slot="{ row: { original: report } }"
								:fieldFn="(report) => report.space?.name || ''"
								:header="t('object.space')"
								enableColumnFilter
							>
								<div class="flex flex-wrap gap-1">
									<LinkButton
										v-if="report.space"
										color="primary"
										variant="soft"
										size="xs"
										:to="{ name: 'spaces-id', params: { id: report.space.id } }"
									>
										{{ report.space.name }}
									</LinkButton>
								</div>
							</Column>
						</ScopeGuard>
					</DesktopOnly>

					<Column
						v-slot="{ row: { original: report } }"
						field="enabled"
						:header="t('report.enabled')"
						enableColumnFilter
					>
						<Icon v-if="report.enabled" class="text-success" :d="mdiCheckCircleOutline" />
					</Column>

					<Column v-slot="{ row: { original: report } }" shrink>
						<div class="flex gap-1">
							<SubscriptionToggleButton
								v-if="canSubscribe"
								:resource="{ id: report.id, type: 'report' }"
								:tooltip="report.isSubscribed ? t('common.unsubscribe') : t('common.subscribe')"
							/>
							<ReportNavButton :id="report.id" size="sm" />
						</div>
					</Column>
				</template>
			</AppDataTable>
		</Frame>
		<CreateReportDialog v-model:dialog="createReportDialog" :scope="scope" @created="goToReport" />
	</DrawerLayout>
</template>
